






























import { defineComponent, computed, ref, onBeforeUnmount } from "@vue/composition-api";
import { i18n, setI18nLocale, supportedLanguages, languageLabels } from "@/plugins/i18n";

/**
 * Language switcher for unauthenticated visitors only (login, registration, etc.). Once a
 * patient logs in, PreferredLanguageCode is the source of truth for the UI locale instead.
 *
 * Hand-rolled dropdown instead of Bootstrap's: `<header>` sets `overflow: auto` for mobile
 * nav scrolling, which clips a Popper-positioned (`position: absolute`) `.dropdown-menu`.
 * `position: fixed` here avoids that without needing Bootstrap 5.2+'s `data-bs-strategy`.
 */
export default defineComponent({
  name: "LanguageToggle",
  setup() {
    const locale = computed(() => i18n.locale);
    const languageCode = computed(() => locale.value.split("-")[0]);
    const options = computed(() => supportedLanguages.value ?? []);
    const open = ref(false);
    const triggerEl = ref<HTMLElement | null>(null);
    const menuStyle = ref<Record<string, string>>({});

    function positionMenu() {
      const el = triggerEl.value;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      menuStyle.value = {
        position: "fixed",
        display: "block",
        top: `${rect.bottom + 4}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
    }

    function onDocumentClick(event: MouseEvent) {
      if (triggerEl.value && !triggerEl.value.contains(event.target as Node)) {
        close();
      }
    }

    function onKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }

    function addListeners() {
      document.addEventListener("click", onDocumentClick, true);
      document.addEventListener("keydown", onKeydown);
      window.addEventListener("resize", positionMenu);
      window.addEventListener("scroll", positionMenu, true);
    }

    function removeListeners() {
      document.removeEventListener("click", onDocumentClick, true);
      document.removeEventListener("keydown", onKeydown);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    }

    function close() {
      if (!open.value) return;
      open.value = false;
      removeListeners();
    }

    function toggle() {
      if (open.value) {
        close();
        return;
      }
      open.value = true;
      positionMenu();
      addListeners();
    }

    function changeLanguage(languageCode: string) {
      setI18nLocale(languageCode);
      close();
    }

    onBeforeUnmount(removeListeners);

    return { locale, languageCode, options, languageLabels, open, triggerEl, menuStyle, toggle, changeLanguage };
  },
});
